import React from 'react';
import { Link } from 'react-router-dom';
import {
  AppleIcon,
  DocIcon,
  FlaskIcon,
  HistoryIcon,
  PulseIcon,
  SectionCard,
} from '../../layout/memberPrimitives';
import type { ActivityEntry, ActivityIcon } from '../../../utils/dashboard/dashboardActivity';

const ICONS: Record<ActivityIcon, React.ComponentType<{ size?: number }>> = {
  flask: FlaskIcon,
  pulse: PulseIcon,
  doc: DocIcon,
  history: HistoryIcon,
  apple: AppleIcon,
};

/**
 * What has happened on the record lately, oldest concern last.
 *
 * Sits at the bottom deliberately: it is reassurance that the practice is
 * working, not something the member needs to act on. Renders nothing at all
 * when there is no history — an empty activity card is worse than no card.
 */
export default function RecentActivityList({ entries }: { entries: ActivityEntry[] }) {
  if (entries.length === 0) return null;

  return (
    <SectionCard eyebrow="Recent activity" title="On your record">
      <ol className="list-none p-0 m-0">
        {entries.map((entry, index) => {
          const Icon = ICONS[entry.icon];
          const row = (
            <>
              <span
                className="shrink-0 flex items-center justify-center"
                style={{ width: 28, height: 28, color: 'var(--melio-olive-deep)' }}
                aria-hidden="true"
              >
                <Icon size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-sm block" style={{ color: 'var(--melio-ink)' }}>
                  {entry.title}
                </span>
                {entry.detail && (
                  <span className="text-[11px] block" style={{ color: 'var(--melio-text-muted)' }}>
                    {entry.detail}
                  </span>
                )}
              </span>
              <span
                className="text-xs tabular-nums shrink-0"
                style={{ color: 'var(--melio-text-muted)' }}
              >
                {entry.dateLabel}
              </span>
            </>
          );

          return (
            <li
              key={entry.id}
              className="py-2.5"
              style={{ borderTop: index === 0 ? 'none' : '1px solid var(--melio-border)' }}
            >
              {entry.href ? (
                <Link to={entry.href} className="flex items-center gap-3">
                  {row}
                </Link>
              ) : (
                <div className="flex items-center gap-3">{row}</div>
              )}
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}
